import { useState } from 'react';
import { HelpCircle, Plus, Edit, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQ_CATEGORIES = ['Shipping', 'Pricing', 'Tracking', 'Customs', 'Insurance', 'General'];

const INITIAL_FORM = {
  question: '', question_es: '', answer: '', answer_es: '',
  category: '', order: 0, is_published: true
};

const FaqManager = ({ faqs, onRefresh, isSpanish }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const resetForm = () => setForm(INITIAL_FORM);

  const openEdit = (faq) => {
    setEditing(faq);
    setForm({
      question: faq.question || '',
      question_es: faq.question_es || '',
      answer: faq.answer || '',
      answer_es: faq.answer_es || '',
      category: faq.category || '',
      order: faq.order || 0,
      is_published: faq.is_published !== false
    });
    setIsDialogOpen(true);
  };

  const openNew = () => {
    resetForm();
    setEditing(null);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const url = editing 
        ? `${API_URL}/api/admin/cms/faq/${editing._id}`
        : `${API_URL}/api/admin/cms/faq`;
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(form), credentials: 'include' });
      
      if (res.ok) {
        toast.success(isSpanish ? 'FAQ guardado' : 'FAQ saved successfully');
        setIsDialogOpen(false);
        setEditing(null);
        resetForm();
        onRefresh();
      } else {
        toast.error(isSpanish ? 'Error al guardar' : 'Failed to save');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm(isSpanish ? '¿Eliminar esta pregunta?' : 'Delete this FAQ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/faq/${faqId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'FAQ eliminado' : 'FAQ deleted');
        onRefresh();
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  return (
    <>
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{isSpanish ? 'Preguntas Frecuentes' : 'FAQ Items'}</CardTitle>
          <Button onClick={openNew} className="btn-accent">
            <Plus className="w-4 h-4 mr-2" />
            {isSpanish ? 'Nueva Pregunta' : 'New FAQ'}
          </Button>
        </CardHeader>
        <CardContent>
          {faqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-steel-300 mx-auto mb-4" />
              <p className="text-steel-600">{isSpanish ? 'No hay FAQs' : 'No FAQs yet'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isSpanish ? 'Pregunta' : 'Question'}</TableHead>
                  <TableHead>{isSpanish ? 'Categoría' : 'Category'}</TableHead>
                  <TableHead>{isSpanish ? 'Orden' : 'Order'}</TableHead>
                  <TableHead>{isSpanish ? 'Estado' : 'Status'}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {faqs.map(faq => (
                  <TableRow key={faq._id}>
                    <TableCell className="max-w-md">
                      <span className="font-medium line-clamp-2">{faq.question}</span>
                    </TableCell>
                    <TableCell>
                      <span className="bg-steel-100 px-2 py-1 rounded text-sm">{faq.category}</span>
                    </TableCell>
                    <TableCell>{faq.order || 0}</TableCell>
                    <TableCell>
                      {faq.is_published ? (
                        <span className="text-green-600 flex items-center gap-1"><Eye className="w-4 h-4" /> Published</span>
                      ) : (
                        <span className="text-steel-400 flex items-center gap-1"><EyeOff className="w-4 h-4" /> Draft</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(faq)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(faq._id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* FAQ Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? (isSpanish ? 'Editar FAQ' : 'Edit FAQ') : (isSpanish ? 'Nueva Pregunta' : 'New FAQ')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="space-y-4">
              <div>
                <Label>{isSpanish ? 'Pregunta (Inglés)' : 'Question (English)'}</Label>
                <Textarea value={form.question} onChange={(e) => setForm({...form, question: e.target.value})} rows={2} />
              </div>
              <div>
                <Label>{isSpanish ? 'Respuesta (Inglés)' : 'Answer (English)'}</Label>
                <Textarea value={form.answer} onChange={(e) => setForm({...form, answer: e.target.value})} rows={4} />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label>{isSpanish ? 'Pregunta (Español)' : 'Question (Spanish)'}</Label>
                <Textarea value={form.question_es} onChange={(e) => setForm({...form, question_es: e.target.value})} rows={2} />
              </div>
              <div>
                <Label>{isSpanish ? 'Respuesta (Español)' : 'Answer (Spanish)'}</Label>
                <Textarea value={form.answer_es} onChange={(e) => setForm({...form, answer_es: e.target.value})} rows={4} />
              </div>
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <div>
                <Label>{isSpanish ? 'Categoría' : 'Category'}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {FAQ_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{isSpanish ? 'Orden' : 'Order'}</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({...form, is_published: e.target.checked})} className="rounded" />
                  <span>{isSpanish ? 'Publicado' : 'Published'}</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" /> {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} className="btn-accent">
              <Save className="w-4 h-4 mr-2" /> {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FaqManager;
